import {useState, useEffect, useContext} from 'react';
import Slide from '@mui/material/Slide';
import {NewProjectContext} from '../utils/NewProjectContext';
import ActionButtons from './ActionButtons';
import {CUSTOM_EVENT_NAMES, fireCustomEvent} from '../../../util/Analytics';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import {userState} from '../../../recoil/user/userAtom';
import {useRecoilValue} from 'recoil';
import * as Sentry from '@sentry/react';
import {API_URL} from '../../../constants';
import MultipleChoice from '../templateInputs/MultipleChoice';

interface IProps {
  changeStepHandler: (number: number) => void;
  forward: boolean;
}

const ProjectInvolvement = ({changeStepHandler, forward}: IProps) => {
  useEffect(() => {
    fireCustomEvent(CUSTOM_EVENT_NAMES.NEW_PROJECT_WIZARD_INVOLVEMENT);
  }, []);

  const user = useRecoilValue(userState);
  const {project, setProject, setNewProjectStep} =
    useContext(NewProjectContext);

  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [value, setValue] = useState<string>('');

  const savedDetail =
    project.details &&
    project.details.find(
      (detail: $TSFixMe) =>
        detail.prompt === 'Project involvement expectations:',
    );

  const handleClickNext = () => {
    setDisabled(true);
    setLoading(true);
    const body = {
      value: value,
      type: 'SHORT_FORM_TEXT',
      prompt: 'Project involvement expectations:',
    };

    const url = !savedDetail
      ? `${API_URL}/rfps/${project.uid}/details`
      : `${API_URL}/rfps/details/${savedDetail?.id}`;

    securedFetchRequest(url, {
      method: !savedDetail ? 'POST' : 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((res) => res.json())
      .then((res) => {
        setDisabled(false);
        setLoading(false);
        localStorage.setItem('rfp', JSON.stringify(res));
        setProject(res);
        changeStepHandler(1);
      })
      .catch((err) => {
        setLoading(false);
        process.env.NODE_ENV === 'development' && console.error(err);

        Sentry.withScope(function (scope) {
          scope.setUser({
            uid: user?.uid,
            firstName: user?.firstName,
          });
          scope.setTag('section', 'create_user_step_2');
          Sentry.captureException(err);
        });
      });
  };

  useEffect(() => {
    if (value) {
      handleClickNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Slide in={true} direction={forward ? 'left' : 'right'} timeout={400}>
      <div style={{width: '100%', maxWidth: 450}}>
        <MultipleChoice
          key={'project_involvement'}
          value={value}
          setValue={setValue}
          label={'How hands-on do you want to be during this project?'}
          options={[
            {
              label: 'I prefer to oversee every detail',
              value: 'I prefer to oversee every detail',
            },
            {
              label:
                'I want updates but trust the contractor to handle most decisions',
              value:
                'I want updates but trust the contractor to handle most decisions',
            },
            {
              label:
                'I want the contractor to manage everything with minimal input from me',
              value:
                'I want the contractor to manage everything with minimal input from me',
            },
          ]}
        />
        <ActionButtons
          secondaryAction={() => setNewProjectStep(0)}
          primaryAction={handleClickNext}
          disabled={disabled}
          loading={loading}
        />
      </div>
    </Slide>
  );
};

export default ProjectInvolvement;
